/**
 * 🔒 VAULT-SEAL-ADJACENT — DO NOT MODIFY (Feb 24 2026). See /app/memory/VAULT_SEAL.md.
 * Sealed until the user types "Upgrade Vault".
 */

import React, { useEffect, useState } from 'react';
import { Lock, ShieldAlert, Calendar, CheckCircle2, X, FileBadge, UploadCloud } from 'lucide-react';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const DOC_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'drivers_license', label: 'Driver\'s License' },
  { value: 'national_id', label: 'National ID' },
  { value: 'other', label: 'Other government-issued ID' },
];

const VaultRequestModal = ({ mode = 'request', onClose, onSubmitted, onReverified }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [dob, setDob] = useState('');
  const [reason, setReason] = useState('');
  const [docType, setDocType] = useState('passport');
  const [docFile, setDocFile] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const isReverify = mode === 'reverify';
  const today = new Date();
  const maxDob = today.toISOString().slice(0, 10);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) {
      setError('ID document must be ≤ 15 MB.');
      return;
    }
    setError('');
    setDocFile(f);
  };

  const submit = async () => {
    setError('');
    if (!dob) {
      setError('Please enter your date of birth.');
      return;
    }
    setSubmitting(true);
    try {
      let res;
      if (isReverify) {
        res = await authFetch(`${BACKEND_URL}/api/vault/age-reverify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dob }),
        });
      } else {
        const fd = new FormData();
        fd.append('dob', dob);
        fd.append('reason', reason);
        if (docFile) {
          fd.append('id_doc_type', docType);
          fd.append('id_doc', docFile);
        }
        res = await fetch(`${BACKEND_URL}/api/vault/request`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
      }
      const data = await res.json();
      if (!res.ok) {
        setError(data.detail || 'Failed to submit.');
        return;
      }
      if (isReverify) {
        onReverified?.();
        onClose?.();
        return;
      }
      setSuccess(true);
      onSubmitted?.(data);
    } catch {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="vault-request-modal"
    >
      <div className="w-full max-w-md bg-slate-900 border border-fuchsia-500/40 rounded-2xl shadow-[0_0_60px_-15px_rgba(217,70,239,0.45)] overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-fuchsia-600/20 border border-fuchsia-500/40 flex items-center justify-center">
              <Lock className="w-4 h-4 text-fuchsia-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">
                {isReverify ? 'Re-verify your age' : 'Request Secret Vault'}
              </h3>
              <p className="text-xs text-gray-400">
                {isReverify
                  ? '18+ confirmation expires every 30 days'
                  : '18+ only · admin will review'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            data-testid="vault-request-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!success ? (
          <div className="px-5 py-5 space-y-4">
            {!isReverify && (
              <p className="text-sm text-gray-300 leading-relaxed">
                The vault contains private content reserved for verified adults. Submit your
                date of birth — and optionally an ID — and an administrator will review your
                request and grant a personal access code if approved.
              </p>
            )}

            <label className="block">
              <span className="text-sm font-semibold text-gray-200 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-fuchsia-300" /> Date of birth
              </span>
              <input
                type="date"
                value={dob}
                max={maxDob}
                onChange={(e) => setDob(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-fuchsia-500 [color-scheme:dark]"
                data-testid="vault-request-dob"
              />
            </label>

            {!isReverify && (
              <label className="block">
                <span className="text-sm font-semibold text-gray-200">
                  Why are you requesting access? <span className="text-gray-500 text-xs">(optional)</span>
                </span>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value.slice(0, 500))}
                  rows={3}
                  placeholder="e.g. interested in the cinematic series catalogue"
                  className="mt-1 w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-500 resize-none"
                  data-testid="vault-request-reason"
                />
                <span className="text-[11px] text-gray-500">{reason.length}/500</span>
              </label>
            )}

            {/* Optional ID upload — speeds up admin approval */}
            {!isReverify && (
              <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-900/15 p-3" data-testid="vault-id-upload-section">
                <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                  <FileBadge className="w-3.5 h-3.5 text-fuchsia-300" />
                  Upload an ID
                  <span className="text-[10px] uppercase tracking-wider text-fuchsia-300 font-bold ml-1">Optional · faster approval</span>
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5 mb-2">
                  Passport, driver's license, national ID, or any government-issued document. The file is stored privately and deleted after admin review.
                </p>
                <select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="w-full mb-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-fuchsia-500"
                  data-testid="vault-id-doctype"
                >
                  {DOC_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <label className="flex items-center justify-center gap-2 px-3 py-3 bg-slate-800 border-2 border-dashed border-slate-700 hover:border-fuchsia-500 rounded-lg cursor-pointer text-sm text-gray-300 hover:text-white">
                  <UploadCloud className="w-4 h-4" />
                  {docFile ? docFile.name : 'Click to choose a file'}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="vault-id-file-input"
                  />
                </label>
                {docFile && (
                  <button
                    onClick={() => setDocFile(null)}
                    className="text-[11px] text-red-300 hover:text-red-200 mt-1"
                    data-testid="vault-id-remove"
                  >
                    Remove file
                  </button>
                )}
              </div>
            )}

            {error && (
              <p className="text-xs text-red-400 flex items-center gap-1.5" data-testid="vault-request-error">
                <ShieldAlert className="w-3.5 h-3.5" /> {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !dob}
                className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                data-testid="vault-request-submit"
              >
                {submitting ? 'Submitting…' : isReverify ? 'Confirm' : 'Submit Request'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-6 text-center space-y-3">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-300" />
            </div>
            <h4 className="text-base font-semibold text-white">Request submitted</h4>
            <p className="text-sm text-gray-300">
              An admin will review your request shortly. You'll receive your personal access code
              once approved.
            </p>
            <Button onClick={onClose} className="bg-slate-700 hover:bg-slate-600 text-white">
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VaultRequestModal;
